import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import FormProvider, { RHFSwitch, RHFTextField } from 'components/hook-form';
import { IConfig, ICreateConfig } from '../../../../@types/config';
import RHFMarkdown from 'components/hook-form/RHFMarkdown';
import RHFJSONEditor from 'components/hook-form/RHFJSONEditor';
import { useState } from 'react';
import Iconify from 'components/iconify';

// ----------------------------------------------------------------------
type Props = {
  onSubmit: (payload: ICreateConfig) => void;
  config?: IConfig | null;
  type?: string;
  nameDefault?: string;
};
type FormValuesProps = {
  name: string;
  type: string;
  value: string;
  description: string;
  isPublic: boolean;
};

const renderEditValue = (type: 'json' | 'markdown' | any) => {
  switch (type) {
    case 'json':
      return <RHFJSONEditor name="value" label="Giá trị" />;
    case 'markdown':
      return <RHFMarkdown name="value" style={{ minHeight: 500 }} />;
    default:
      return <RHFTextField name="value" label="Giá trị" size="small" />;
  }
};

export default function ConfigForm({ onSubmit, config, type, nameDefault }: Props) {
  const [typeEdit, setTypeEdit] = useState<string>(type ?? '');
  const ConfigSchema = Yup.object().shape({
    name: Yup.string().required('Thông tin bắt buộc'),
    value: Yup.string().required('Thông tin bắt buộc'),
    type: Yup.string(),
    description: Yup.string(),
    isPublic: Yup.boolean(),
  });

  const defaultValues = {
    name: config?.name ?? nameDefault ?? '',
    value: config?.value ?? '',
    type: config?.type ?? '',
    description: config?.description ?? '',
    isPublic: config?.isPublic ?? false,
  };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(ConfigSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={1}>
        <RHFTextField name="name" label="Tên" size="small" disabled={!!config?.id} />
        <RHFTextField name="type" label="Kiểu" size="small" />
        <RHFTextField multiline rows={3} name="description" label="Mô tả" size="small" />
        <Stack direction="row" spacing={1}>
          <Tooltip title="Sử dụng TextEdit để sửa">
            <IconButton
              disabled={!['markdown', 'json'].includes(typeEdit)}
              color="primary"
              onClick={() => setTypeEdit('')}
            >
              <Iconify icon="material-symbols:input" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sử dụng JSON Viewer để sửa">
            <IconButton
              disabled={typeEdit === 'json'}
              onClick={() => setTypeEdit('json')}
              color="warning"
            >
              <Iconify icon="bxs:file-json" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sử dụng Markdown để sửa" color="error">
            <IconButton disabled={typeEdit === 'markdown'} onClick={() => setTypeEdit('markdown')}>
              <Iconify icon="mdi:language-markdown-outline" />
            </IconButton>
          </Tooltip>
        </Stack>
        {renderEditValue(typeEdit)}
        <RHFSwitch name="isPublic" label="Public or Private" />
      </Stack>
      <Stack alignItems="flex-end" sx={{ my: 2 }}></Stack>
      <LoadingButton
        fullWidth
        color="inherit"
        size="small"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          bgcolor: 'text.primary',
          color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          },
        }}
      >
        {config?.id ? 'Cập nhật' : 'Tạo mới'}
      </LoadingButton>
    </FormProvider>
  );
}
